import React, { useState, useRef, useEffect } from 'react';
import { useLanguage, Language, COUNTRY_TO_LANG } from '../contexts/LanguageContext';

const LanguageSwitcher: React.FC = () => {
  const { language, setLanguage, intlRates } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Wszystkie dostępne języki z flagami
  const ALL_LANGUAGES: { code: Language; name: string; flag: string }[] = [
    { code: 'pl', name: 'Polski',      flag: 'https://flagcdn.com/w40/pl.png' },
    { code: 'de', name: 'Deutsch',     flag: 'https://flagcdn.com/w40/de.png' },
    { code: 'cs', name: 'Čeština',    flag: 'https://flagcdn.com/w40/cz.png' },
    { code: 'sk', name: 'Slovenčina', flag: 'https://flagcdn.com/w40/sk.png' },
    { code: 'fr', name: 'Français',   flag: 'https://flagcdn.com/w40/fr.png' },
    { code: 'nl', name: 'Nederlands', flag: 'https://flagcdn.com/w40/nl.png' },
    { code: 'it', name: 'Italiano',   flag: 'https://flagcdn.com/w40/it.png' },
    { code: 'ro', name: 'Română',     flag: 'https://flagcdn.com/w40/ro.png' },
    { code: 'hu', name: 'Magyar',     flag: 'https://flagcdn.com/w40/hu.png' },
    { code: 'hr', name: 'Hrvatski',   flag: 'https://flagcdn.com/w40/hr.png' },
    { code: 'lt', name: 'Lietuvių',   flag: 'https://flagcdn.com/w40/lt.png' },
    { code: 'lv', name: 'Latviešu',   flag: 'https://flagcdn.com/w40/lv.png' },
    { code: 'et', name: 'Eesti',      flag: 'https://flagcdn.com/w40/ee.png' },
    { code: 'en', name: 'English',    flag: 'https://flagcdn.com/w40/gb.png' },
    { code: 'es', name: 'Español',    flag: 'https://flagcdn.com/w40/es.png' },
    { code: 'uk', name: 'Українська', flag: 'https://flagcdn.com/w40/ua.png' },
  ];

  // Filtruj: zawsze 'pl' + języki krajów do których wysyłamy
  const activeLangCodes = new Set<Language>(['pl']);
  intlRates.forEach(r => {
    const lang = COUNTRY_TO_LANG[r.country_code];
    if (lang) activeLangCodes.add(lang);
  });
  // Fallback: jeśli brak danych z API, pokaż wszystkie
  const languages = intlRates.length > 0
    ? ALL_LANGUAGES.filter(l => activeLangCodes.has(l.code))
    : ALL_LANGUAGES;

  const currentLang = languages.find(l => l.code === language) || languages[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (lang: Language) => {
    setLanguage(lang);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="fc-language-trigger flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-cyan-500/15 hover:bg-cyan-500/20 border border-cyan-500/30 hover:border-cyan-400/50 transition-all duration-200"
          aria-expanded={isOpen}
          aria-label="Wybierz język / Select language"
        >
          <img src={currentLang.flag} alt="" width="20" height="13" className="w-5 h-auto rounded-sm" />
          <span className="text-white text-xs font-medium hidden sm:inline">{currentLang.code.toUpperCase()}</span>
          <svg 
            className={`w-4 h-4 text-cyan-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isOpen && (
          <div className="fc-language-dropdown absolute top-full right-0 mt-1 w-48 bg-[#111827] border border-gray-600/50 rounded-lg shadow-xl z-50 py-1 backdrop-blur-sm">
            {languages.map(lang => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                data-active={language === lang.code}
                className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-cyan-500/10 transition-colors ${
                  language === lang.code ? 'fc-language-option bg-cyan-500/20 text-cyan-200' : 'fc-language-option text-white'
                }`}
              >
                <img src={lang.flag} alt="" width="20" height="13" className="w-5 h-auto rounded-sm" />
                <span className="text-sm font-medium">{lang.name}</span>
                {language === lang.code && (
                  <svg className="w-5 h-5 ml-auto text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
  );
};

export default LanguageSwitcher;